import {
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Warning, CheckCircleOutline } from '@material-ui/icons';
import { Button, StandardDialog, TextField } from '@roc/ui';
import {
  APPRAISAL_TYPE_OPTIONS,
  FINAL_INSPECTION_APPRAISAL_TYPE,
  LAND_APPRAISAL_TYPE,
} from '../../utils/appraisalDetailsV2Constants';
import { AppraisalDetailsV2Store } from 'libs/feature-documents/src/documents/stores/documentForms/appraisalDetailsV2/appraisalDetailsV2Store';
import { AMCEnum } from '@roc/feature-utils';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';

const useStyles = makeStyles(theme => ({
  infoBox: {
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  successBox: {
    backgroundColor: '#e8f5e9',
  },
  warningBox: {
    backgroundColor: '#fff8e1',
  },
  infoText: {
    fontSize: '14px',
    lineHeight: 1.5,
  },
  loanDetail: {
    fontWeight: 700,
  },
  confirmText: {
    fontSize: '14px',
    lineHeight: 1.5,
    marginTop: theme.spacing(1),
  },
  questionText: {
    fontSize: '14px',
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  radioGroup: {
    flexDirection: 'row',
  },
}));

export const AddAppraisalDialog = observer(
  ({
    store,
    open,
    onClose,
    loanId,
    canLinkOrder,
    canOrderFinalInspection,
    canPushNewOrder,
    selectedAmcId,
    onSuccess,
  }: {
    store: AppraisalDetailsV2Store;
    open: boolean;
    onClose: () => void;
    loanId: number;
    canLinkOrder: boolean;
    canOrderFinalInspection: boolean;
    canPushNewOrder: boolean;
    selectedAmcId?: number;
    onSuccess?: () => void;
  }) => {
    const classes = useStyles();
    const actionsStore = store.appraisalActionsStore;

    const [selectedPropertyId, setSelectedPropertyId] = useState<number | ''>('');
    const [appraisalType, setAppraisalType] = useState('');
    const [propertyError, setPropertyError] = useState('');
    const [appraisalTypeError, setAppraisalTypeError] = useState('');
    const [isExistingOrder, setIsExistingOrder] = useState<boolean | null>(null);
    const [orderNumber, setOrderNumber] = useState('');
    const [orderError, setOrderError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
      if (open) {
        setSelectedPropertyId('');
        setAppraisalType('');
        setPropertyError('');
        setAppraisalTypeError('');
        setIsExistingOrder(null);
        setOrderNumber('');
        setOrderError('');
        setSubmitting(false);
        actionsStore.resetLinkOrder();
      }
    }, [open]);

    const isFinalInspection = appraisalType === FINAL_INSPECTION_APPRAISAL_TYPE;
    const showExistingOrderQuestion =
      !!appraisalType && !isFinalInspection && canLinkOrder && canPushNewOrder;
    const resolvedIsExistingOrder = showExistingOrderQuestion
      ? isExistingOrder
      : !isFinalInspection && !!appraisalType
      ? canLinkOrder
      : null;

    const handleAppraisalTypeChange = (value: string) => {
      setAppraisalType(value);
      setIsExistingOrder(null);
      setOrderNumber('');
      setOrderError('');
      actionsStore.resetLinkOrder();
    };

    const lookupResult = actionsStore.linkOrderLookupResult;
    const isOrderValid = lookupResult?.orderExists === true;
    const isOrderInactive = lookupResult?.isOrderInNegativeStatus === true && lookupResult?.paymentsCompleted === true;
    const isResultFound = isOrderValid && lookupResult?.resultFound === true;
    const isLinkedToLoan = isResultFound && lookupResult?.loanId != null;
    const isLinkedToStandalone = isResultFound && lookupResult?.loanId == null && lookupResult?.loanPropertyValuationId != null;
    const showAsNotLinked = isOrderValid && !isOrderInactive && !isLinkedToLoan && !isLinkedToStandalone;

    const validateDetails = () => {
      let valid = true;
      if (!selectedPropertyId) {
        setPropertyError('Please select a property');
        valid = false;
      } else {
        setPropertyError('');
      }
      if (!appraisalType) {
        setAppraisalTypeError('Please select an appraisal type');
        valid = false;
      } else {
        setAppraisalTypeError('');
      }
      return valid;
    };

    const handleLookupOrder = async () => {
      if (!validateDetails()) return;
      if (!orderNumber.trim()) {
        setOrderError('Please enter an order number');
        return;
      }
      setOrderError('');
      await flowResult(actionsStore.lookupOrder(orderNumber.trim().toUpperCase(), loanId));
    };

    const handleCreateLpvAndPaymentLink = async (subType: string) => {
      if (!validateDetails()) return;
      setSubmitting(true);
      try {
        const success = await flowResult(
          actionsStore.createLpvAndPaymentLink(loanId, selectedPropertyId as number, subType)
        );
        if (success) {
          onClose();
          onSuccess?.();
        }
      } finally {
        setSubmitting(false);
      }
    };

    const handleSubmitNewOrder = async () => {
      if (!validateDetails()) return;
      setSubmitting(true);
      try {
        const success = await flowResult(
          actionsStore.pushNewOrder(loanId, selectedPropertyId as number, appraisalType)
        );
        if (success) {
          onClose();
          onSuccess?.();
        }
      } finally {
        setSubmitting(false);
      }
    };

    const handleSubmitLinkOrder = async (isPushNewOrder?: boolean) => {
      setSubmitting(true);
      try {
        const success = await flowResult(
          actionsStore.submitLinkOrder({
            loanId,
            propertyId: selectedPropertyId as number,
            orderNumber: orderNumber.trim().toUpperCase(),
            isExistingOrder: lookupResult?.propertyValuationId != null,
            appraisalType,
            loanPropertyValuationId: lookupResult?.loanPropertyValuationId,
            propertyValuationId: lookupResult?.propertyValuationId,
            isPushNewOrder: isPushNewOrder ? true : undefined,
          })
        );
        if (success) {
          onClose();
          onSuccess?.();
        }
      } finally {
        setSubmitting(false);
      }
    };

    const propertyOptions = store.properties.map(p => ({
      label: p.propertyAddress,
      value: p.propertyId,
    }));

    const appraisalTypeOptions = APPRAISAL_TYPE_OPTIONS.filter(opt => {
      if (opt.value === FINAL_INSPECTION_APPRAISAL_TYPE) return canOrderFinalInspection;
      if (opt.value === 'Appraisal') return selectedAmcId === AMCEnum.TAMARISK;
      if (opt.value === LAND_APPRAISAL_TYPE) return selectedAmcId !== AMCEnum.VSAMC && (canLinkOrder || canPushNewOrder);
      return canLinkOrder || canPushNewOrder;
    });

    const dialogContent = (
      <Box minWidth={420} display="flex" flexDirection="column" style={{ gap: '16px' }}>
        <FormControl variant="outlined" fullWidth error={!!propertyError}>
          <InputLabel>Select Property</InputLabel>
          <Select
            value={selectedPropertyId}
            onChange={e => setSelectedPropertyId(e.target.value as number)}
            label="Select Property"
          >
            {propertyOptions.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
          {propertyError && (
            <Typography variant="caption" color="error">
              {propertyError}
            </Typography>
          )}
        </FormControl>

        <FormControl variant="outlined" fullWidth error={!!appraisalTypeError}>
          <InputLabel>Appraisal Type</InputLabel>
          <Select
            value={appraisalType}
            onChange={e => handleAppraisalTypeChange(e.target.value as string)}
            label="Appraisal Type"
          >
            {appraisalTypeOptions.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
          {appraisalTypeError && (
            <Typography variant="caption" color="error">
              {appraisalTypeError}
            </Typography>
          )}
        </FormControl>

        {isFinalInspection && (
          <Box className={`${classes.infoBox} ${classes.warningBox}`}>
            <Warning style={{ color: '#f9a825', marginTop: 2 }} />
            <Typography className={classes.infoText}>
              Please confirm this final inspection (1004D) order once you have
              confirmed the damage was repaired.
            </Typography>
          </Box>
        )}

        {showExistingOrderQuestion && (
          <FormControl component="fieldset">
            <Typography className={classes.questionText}>
              Is this an existing order?
            </Typography>
            <RadioGroup
              className={classes.radioGroup}
              value={isExistingOrder === null ? '' : String(isExistingOrder)}
              onChange={e => setIsExistingOrder(e.target.value === 'true')}
            >
              <FormControlLabel
                value="true"
                control={<Radio color="primary" size="small" />}
                label="Yes"
              />
              <FormControlLabel
                value="false"
                control={<Radio color="primary" size="small" />}
                label="No"
              />
            </RadioGroup>
          </FormControl>
        )}

        {resolvedIsExistingOrder === false && !isFinalInspection && (
          <Box className={`${classes.infoBox} ${classes.warningBox}`}>
            <Warning style={{ color: '#f9a825', marginTop: 2 }} />
            <Typography className={classes.infoText}>
              This will push a new order directly to the AMC. Do you want to proceed?
            </Typography>
          </Box>
        )}

        {resolvedIsExistingOrder === true && (
          <>
            <TextField
              testId="order-number"
              variant="outlined"
              label="Order Number"
              standaloneLabel={false}
              fullWidth
              value={orderNumber}
              onChange={e => setOrderNumber(e.target.value)}
              error={!!orderError}
              helperText={orderError}
            />

            {actionsStore.isLinkOrderLoading && (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={24} />
              </Box>
            )}

            {!actionsStore.isLinkOrderLoading && lookupResult !== null && (
              <>
                {!isOrderValid && (
                  <Box className={`${classes.infoBox} ${classes.warningBox}`}>
                    <Warning style={{ color: '#f9a825', marginTop: 2 }} />
                    <Typography className={classes.infoText}>
                      This order number is not valid. Please enter a valid order number.
                    </Typography>
                  </Box>
                )}

                {isOrderValid && isOrderInactive && (
                  <Box className={`${classes.infoBox} ${classes.warningBox}`}>
                    <Warning style={{ color: '#f9a825', marginTop: 2 }} />
                    <Typography className={classes.infoText}>
                      This order is currently inactive. Would you like to link this order or push a new one?
                    </Typography>
                  </Box>
                )}

                {isOrderValid && !isOrderInactive && (
                  <>
                    {showAsNotLinked && (
                      <Box className={`${classes.infoBox} ${classes.successBox}`}>
                        <CheckCircleOutline style={{ color: '#388e3c', marginTop: 2 }} />
                        <Typography className={classes.infoText}>
                          This order is not associated to any record in the system. You can proceed without any issues.
                        </Typography>
                      </Box>
                    )}

                    {isLinkedToLoan && (
                      <Box className={`${classes.infoBox} ${classes.warningBox}`}>
                        <Warning style={{ color: '#f9a825', marginTop: 2 }} />
                        <Box>
                          <Typography className={classes.infoText}>
                            This order is associated with loan:{' '}
                            <span className={classes.loanDetail}>
                              #{lookupResult.loanId} – {lookupResult.dealName}
                            </span>
                          </Typography>
                          <Typography className={classes.confirmText}>
                            Are you sure you want to proceed? Proceeding will unlink this order from the other loan and associate it with this one.
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {isLinkedToStandalone && (
                      <Box className={`${classes.infoBox} ${classes.warningBox}`}>
                        <Warning style={{ color: '#f9a825', marginTop: 2 }} />
                        <Box>
                          <Typography className={classes.infoText}>
                            This order is associated with a standalone valuation for property:{' '}
                            <span className={classes.loanDetail}>{lookupResult.address}</span>
                          </Typography>
                          <Typography className={classes.confirmText}>
                            Are you sure you want to proceed? Proceeding will associate this order with this loan.
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </Box>
    );

    let mainAction: React.ReactNode = null;

    if (isFinalInspection) {
      mainAction = (
        <Button
          testId="confirm-final-inspection"
          variant="contained"
          color="primary"
          onClick={() => handleCreateLpvAndPaymentLink(FINAL_INSPECTION_APPRAISAL_TYPE)}
          disabled={submitting}
        >
          Order
        </Button>
      );
    } else if (resolvedIsExistingOrder === false) {
      mainAction = (
        <Button
          testId="confirm-new-order"
          variant="contained"
          color="primary"
          onClick={handleSubmitNewOrder}
          disabled={submitting}
        >
          Order
        </Button>
      );
    } else if (resolvedIsExistingOrder === true) {
      if (lookupResult === null) {
        mainAction = (
          <Button
            testId="lookup-order-number"
            variant="contained"
            color="primary"
            onClick={handleLookupOrder}
            disabled={actionsStore.isLinkOrderLoading}
          >
            Look Up
          </Button>
        );
      } else if (!isOrderValid) {
        mainAction = (
          <Button
            testId="retry-link-order"
            variant="contained"
            color="primary"
            onClick={() => {
              setOrderNumber('');
              setOrderError('');
              actionsStore.resetLinkOrder();
            }}
          >
            Try Again
          </Button>
        );
      } else if (isOrderInactive) {
        mainAction = (
          <>
            <Button
              testId="inactive-link-order"
              variant="outlined"
              color="primary"
              onClick={() => handleSubmitLinkOrder(false)}
              style={{ marginRight: 8 }}
              disabled={submitting}
            >
              Link Order
            </Button>
            <Button
              testId="inactive-push-new-order"
              variant="contained"
              color="primary"
              onClick={() => handleSubmitLinkOrder(true)}
              disabled={submitting}
            >
              Push New Order
            </Button>
          </>
        );
      } else if (showAsNotLinked) {
        mainAction = (
          <Button
            testId="submit-link-order"
            variant="contained"
            color="primary"
            onClick={() => handleSubmitLinkOrder(false)}
            disabled={submitting}
          >
            Submit
          </Button>
        );
      } else if (isLinkedToLoan || isLinkedToStandalone) {
        mainAction = (
          <Button
            testId="confirm-link-order"
            variant="contained"
            color="primary"
            onClick={() => handleSubmitLinkOrder(false)}
            disabled={submitting}
          >
            Yes, Proceed
          </Button>
        );
      }
    }

    const dialogActions = (
      <>
        <Button
          testId="cancel-add-appraisal"
          variant="outlined"
          color="primary"
          onClick={onClose}
          style={{ marginRight: 8 }}
        >
          Cancel
        </Button>
        {mainAction}
      </>
    );

    return (
      <StandardDialog
        open={open}
        maxWidth="sm"
        title="Add - Link Appraisal"
        handleClose={onClose}
        dialogContent={dialogContent}
        dialogActions={dialogActions}
      />
    );
  }
);
